import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    DxTextBoxModule,
    DxFormModule,
    DxValidatorModule,
    DxSelectBoxComponent,
    DxDateBoxComponent,
    DxButtonModule,
    DxTagBoxModule,
} from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import { getSizeQualifier } from 'src/app/services/screen.service';
import { DataSources, FileDto, InvoicingClient, PermissionType, UpdateVendorCommand, VendorStatus, VendorTerm } from '@services';
import EnumUtils from '../../../../shared/enum-utils';
import { FileUploadComponent, FormPopupComponent, PicturedItemSelectBoxComponent } from 'src/app/components';
import { VendorTermPipe } from 'src/app/pipes/vendor-term.pipe';
import { DATE_FORMAT, MAX_FILE_SIZE } from 'src/app/shared/constants';
import notify from 'devextreme/ui/notify';
import { FileUtils } from 'src/app/shared/file-utils';

@Component({
    selector: 'vendor-form',
    imports: [
        DxTextBoxModule,
        DxFormModule,
        DxValidatorModule,
        FormPopupComponent,
        CommonModule,
        DxSelectBoxComponent,
        DxDateBoxComponent,
        FileUploadComponent,
        DxButtonModule,
        DxTagBoxModule,
        PicturedItemSelectBoxComponent,
    ],
    providers: [VendorTermPipe],
    templateUrl: './vendor-form.component.html',
})
export class VendorFormComponent implements OnChanges {
    @Input() visible = false;
    @Input() vendorId: string | null = null;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onSave = new EventEmitter();    
    @Input() titlePrefix: string | null = null;
    @Input() isReadOnly = false;

    private vendorTermPipe = inject(VendorTermPipe);
    private invoicingClient = inject(InvoicingClient);
    private dataSources = inject(DataSources);

    dateFormat = DATE_FORMAT;
    serviceTypeDataSource = this.dataSources.serviceTypeOptions();
    siteDataSource = this.dataSources.siteOptions(PermissionType.Vendors);
    glCodeDataSource: DataSource = this.buildGlCodeDataSource();
    vendorEmployeeDataSource = this.dataSources.vendorEmployeeOptions();
    maxFileSize = MAX_FILE_SIZE;
    maxFileNumber = 3;
    existingFiles: FileDto[] | null = null;
    newFiles: File[] = [];
    filesToRemove: string[] = [];
    formData: UpdateVendorCommand = { status: VendorStatus.Active } as UpdateVendorCommand;

    vendorStatusOptions = EnumUtils.getEnumOptions(VendorStatus);
    vendorTermOptions = EnumUtils.getEnumOptions(VendorTerm, (v) =>
        this.vendorTermPipe.transform(v),
    );

    getSizeQualifier = getSizeQualifier;

    ngOnChanges(changes: SimpleChanges) {
        const visibleOpened = !!changes['visible'] && changes['visible'].currentValue === true;
        const vendorIdChanged = !!changes['vendorId'];

        if (visibleOpened || vendorIdChanged) {
            this.vendorEmployeeDataSource = this.dataSources.vendorEmployeeOptions(this.vendorId);
        }

        if (this.visible && (visibleOpened || vendorIdChanged)) {
            if(this.vendorId)
            {
                this.loadVendor(this.vendorId);
            } else {
                this.resetForm();
            }
        }
    }

    private loadVendor(vendorId: string) {
        this.invoicingClient.getVendor(vendorId).subscribe({
            next: (vendor) => {
                this.formData = new UpdateVendorCommand({ ...vendor });
                this.existingFiles = vendor.contractFiles ?? [];
                this.formData.contractFiles = [];
                this.filesToRemove = [];
                this.newFiles = [];
                this.glCodeDataSource = this.buildGlCodeDataSource();
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message || 'Failed to load vendor';
                notify(msg, 'error', 3000);
            },
        });
    }

    private resetForm() {
        this.formData = { status: VendorStatus.Active, siteIds: [] } as unknown as UpdateVendorCommand;
        this.existingFiles = [];
        this.newFiles = [];
        this.filesToRemove = [];
        this.glCodeDataSource = this.buildGlCodeDataSource();
    }

    // The Default GL Code dropdown only lists GL codes whose Site (client) is among the selected Sites.
    private buildGlCodeDataSource(): DataSource {
        const siteIds = this.formData?.siteIds ?? [];
        const filter = siteIds.length
            ? siteIds.flatMap((id, i) => (i === 0 ? [['clientId', '=', id]] : ['or', ['clientId', '=', id]]))
            : null;

        return new DataSource({
            store: this.dataSources.glCodeOptions(PermissionType.Vendors),
            filter: filter ?? undefined,
            paginate: true,
        });
    }

    onSitesChanged(e: { event?: unknown }) {
        this.glCodeDataSource = this.buildGlCodeDataSource();

        // Only clear a previously selected GL code when the Site change came from the user, so it can be
        // re-picked against the new Site filter; a programmatic load keeps the persisted GL code.
        if (e.event) {
            this.formData.glCodeId = undefined;
        }
    }

    async onFilesChanged(files: File[]) {
        this.newFiles = files;
        this.formData.contractFiles = await FileUtils.mapFilesToDto(this.newFiles);
    }

    onFilesToRemoveChanged(filesToRemove: string[]) {
        this.filesToRemove = filesToRemove;
        this.formData.contractFilesToRemove = filesToRemove;
    }

    onPopupSaveClick(){
        this.onSave.emit();
    }

    onVisibleChange(visible: boolean) {
        if (!visible) {
            this.filesToRemove = [];
            this.newFiles = [];
        }

        this.visibleChange.emit(visible);
    }

    getData = () => ({ ...this.formData }) as UpdateVendorCommand;
}
